import { ids } from '@stage/contracts';
import type { SetlistItem, SetlistItemType, SetlistSection } from '@stage/contracts';

/**
 * Pure, deterministic set-list editing helpers (§17.1, §17.6). All functions return new arrays and
 * never mutate their inputs, so React state updates stay predictable and the logic is trivially
 * testable without a DOM. `sortOrder` is a single monotonically increasing sequence spanning every
 * item across all sections, reassigned after any structural change.
 */

export type MoveDirection = 'up' | 'down';

/** Reassign a global `sortOrder` (0-based) across all items in document order. */
export function reindexSortOrder(sections: SetlistSection[]): SetlistSection[] {
  let order = 0;
  return sections.map((section) => ({
    ...section,
    items: section.items.map((item) => ({ ...item, sortOrder: order++ })),
  }));
}

/** Locate an item and its position. Returns null when the item is absent. */
function locate(
  sections: SetlistSection[],
  itemId: string,
): { sectionIndex: number; itemIndex: number } | null {
  for (let s = 0; s < sections.length; s += 1) {
    const items = sections[s]!.items;
    for (let i = 0; i < items.length; i += 1) {
      if (items[i]!.itemId === itemId) return { sectionIndex: s, itemIndex: i };
    }
  }
  return null;
}

function withItems(section: SetlistSection, items: SetlistItem[]): SetlistSection {
  return { ...section, items };
}

/**
 * Move an item one step `up` or `down`. Within a section this swaps with its neighbour; at a
 * section boundary the item hops to the adjacent section (end of the previous / start of the next),
 * enabling keyboard reordering across the whole list (§17.6, §35). No-op at the very top/bottom.
 * `sortOrder` is reindexed on every successful move.
 */
export function moveItem(
  sections: SetlistSection[],
  itemId: string,
  direction: MoveDirection,
): SetlistSection[] {
  const at = locate(sections, itemId);
  if (!at) return sections;
  const { sectionIndex, itemIndex } = at;
  const items = sections[sectionIndex]!.items;

  if (direction === 'up') {
    if (itemIndex > 0) {
      const next = items.slice();
      [next[itemIndex - 1], next[itemIndex]] = [next[itemIndex]!, next[itemIndex - 1]!];
      return reindexSortOrder(
        sections.map((s, i) => (i === sectionIndex ? withItems(s, next) : s)),
      );
    }
    if (sectionIndex > 0) {
      const moving = items[itemIndex]!;
      return reindexSortOrder(
        sections.map((s, i) => {
          if (i === sectionIndex) return withItems(s, items.slice(1));
          if (i === sectionIndex - 1) return withItems(s, [...s.items, moving]);
          return s;
        }),
      );
    }
    return sections;
  }

  // direction === 'down'
  if (itemIndex < items.length - 1) {
    const next = items.slice();
    [next[itemIndex], next[itemIndex + 1]] = [next[itemIndex + 1]!, next[itemIndex]!];
    return reindexSortOrder(sections.map((s, i) => (i === sectionIndex ? withItems(s, next) : s)));
  }
  if (sectionIndex < sections.length - 1) {
    const moving = items[itemIndex]!;
    return reindexSortOrder(
      sections.map((s, i) => {
        if (i === sectionIndex) return withItems(s, items.slice(0, -1));
        if (i === sectionIndex + 1) return withItems(s, [moving, ...s.items]);
        return s;
      }),
    );
  }
  return sections;
}

/** Default display titles for freshly-created items so the row is never nameless. */
const DEFAULT_TITLES: Record<SetlistItemType, string> = {
  song: 'New song',
  improv_slot: 'Improv slot',
  announcement: 'Announcement',
  break: 'Break',
  custom: 'Custom segment',
};

/** Build a new, fully-defaulted set-list item with a generated id (§17.2). */
export function newItem(type: SetlistItemType, sortOrder: number): SetlistItem {
  return {
    itemId: ids.setlistItem.generate(),
    type,
    displayTitle: DEFAULT_TITLES[type],
    songId: null,
    plannedDurationSeconds: null,
    plannedStartAt: null,
    publicVisible: true,
    requestable: false,
    tempo: null,
    status: 'planned',
    sortOrder,
    version: 0,
  };
}

/** Build a new empty section with a generated id. */
export function newSection(name: string): SetlistSection {
  return { sectionId: `sec_${ids.setlistItem.generate()}`, name, items: [] };
}

/** Append an item of `type` to a section, then reindex. */
export function addItem(
  sections: SetlistSection[],
  sectionId: string,
  type: SetlistItemType,
): SetlistSection[] {
  return reindexSortOrder(
    sections.map((s) =>
      s.sectionId === sectionId ? withItems(s, [...s.items, newItem(type, 0)]) : s,
    ),
  );
}

/** Remove an item by id, then reindex. */
export function removeItem(sections: SetlistSection[], itemId: string): SetlistSection[] {
  return reindexSortOrder(
    sections.map((s) => withItems(s, s.items.filter((it) => it.itemId !== itemId))),
  );
}

/** Apply a partial patch to a single item by id (no reindex needed — order is unchanged). */
export function updateItem(
  sections: SetlistSection[],
  itemId: string,
  patch: Partial<SetlistItem>,
): SetlistSection[] {
  return sections.map((s) =>
    withItems(
      s,
      s.items.map((it) => (it.itemId === itemId ? { ...it, ...patch } : it)),
    ),
  );
}

/** Append a new section. */
export function addSection(sections: SetlistSection[], name: string): SetlistSection[] {
  return [...sections, newSection(name || 'New section')];
}

/** Remove a section (and its items), then reindex. */
export function removeSection(sections: SetlistSection[], sectionId: string): SetlistSection[] {
  return reindexSortOrder(sections.filter((s) => s.sectionId !== sectionId));
}

/** Rename a section by id. */
export function renameSection(
  sections: SetlistSection[],
  sectionId: string,
  name: string,
): SetlistSection[] {
  return sections.map((s) => (s.sectionId === sectionId ? { ...s, name } : s));
}

/** Flatten all items in document order — useful for the console's current/upcoming view. */
export function flattenItems(sections: SetlistSection[]): SetlistItem[] {
  return sections.flatMap((s) => s.items);
}
